import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaPen, FaPlus, FaSearch } from 'react-icons/fa';
import ErpTemplate from '../../components/ErpTemplate';
import api from '../../services/api';
import '../../css/financeiro-parcela-pagar.css';

const TIPOS = {
    disparo: 'Disparo',
    pendencia: 'Pendência',
};

export default function PainelSlaCriterio() {
    const token = localStorage.getItem('token');
    const [filtro, setFiltro] = useState({ nome: '', buscando: false });
    const [criterios, setCriterios] = useState([]);
    const [origens, setOrigens] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const controllerRef = useRef(null);

    const buscar = () => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setFiltro((state) => ({ ...state, buscando: true }));
        api({
            method: 'get',
            action: '/slaCriterio',
            token,
            params: { nome: filtro.nome },
            signal: controllerRef.current.signal
        })
            .then((response) => {
                setCriterios(response.data.data ?? []);
                setPage(1);
                setFiltro((state) => ({ ...state, buscando: false }));
            })
            .catch((err) => {
                // Cancelamento é o abort da busca anterior, não erro.
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setFiltro((state) => ({ ...state, buscando: false }));
                // Sem isto, 403 ou 500 renderizam "Nenhum registro" — indistinguível de base
                // vazia, e o operador conclui que os cadastros sumiram.
                toast.error('Erro ao buscar: ' + (err.response?.data?.error ?? err.message ?? ''));
            });
    };

    useEffect(() => {
        document.querySelector('#nome')?.focus();
        buscar();
        // As origens vêm junto para traduzir a chave em rótulo na listagem. Falha aqui é só
        // cosmética (a chave crua aparece no lugar do rótulo), então não interrompe a tela.
        api({ method: 'get', action: '/slaCriterio/origens', token })
            .then((response) => setOrigens(response.data.data ?? []))
            .catch(() => setOrigens([]));
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
        // eslint-disable-next-line
    }, []);

    const nomeOrigem = (chave) => origens.find((item) => item.origem === chave)?.nome ?? chave;

    const paginados = useMemo(() => {
        const inicio = (page - 1) * pageSize;
        return criterios.slice(inicio, inicio + pageSize);
    }, [criterios, page, pageSize]);

    const totalPaginas = Math.max(1, Math.ceil(criterios.length / pageSize));

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            Critérios de SLA
                            {/* A tela lista colunas do banco, então o acesso é restrito. Sinalizar
                                evita que alguém reporte "sumiu o menu" achando que é bug. */}
                            <span className="fin-pp__pill is-warn" title="Esta tela lista colunas do banco e é restrita ao perfil admin">
                                Somente admin
                            </span>
                        </h1>
                        <p className="fin-pp__subtitle">Catálogo de campos que os alertas usam como gatilho ou pendência.</p>
                    </div>
                    <div className="fin-pp__actions">
                        <Link to="/sla" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaArrowLeft size={13} /> SLA
                        </Link>
                        <Link to="/cadastroSlaCriterio" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                            <FaPlus size={14} /> Novo Critério
                        </Link>
                    </div>
                </div>

                <form
                    className="fin-pp__card fin-pp__search"
                    style={{ marginBottom: 16, padding: '12px 16px' }}
                    onSubmit={(e) => { buscar(); e.preventDefault(); }}
                >
                    <div className="fin-pp__search-field">
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            id="nome"
                            className="fin-pp__input"
                            type="text"
                            placeholder="Buscar pelo nome do critério"
                            value={filtro.nome ?? ''}
                            onChange={(e) => setFiltro((state) => ({ ...state, nome: e.target.value }))}
                        />
                    </div>
                    <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={filtro.buscando}>
                        {filtro.buscando ? 'Buscando...' : 'Buscar'}
                    </button>
                </form>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 900, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 62 }}></th>
                                    <th style={{ width: 100 }}>Situação</th>
                                    <th style={{ width: 120 }}>Tipo</th>
                                    <th>Nome</th>
                                    <th style={{ width: 200 }}>Origem</th>
                                    <th>Campo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtro.buscando ? (
                                    <tr><td colSpan={6} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                ) : paginados.length === 0 ? (
                                    <tr><td colSpan={6} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <Link to={`/cadastroSlaCriterio/${row.id}`} className="fin-pp__rowbtn" title="Alterar critério">
                                                <FaPen size={14} />
                                            </Link>
                                        </td>
                                        <td>
                                            <span className={`fin-pp__pill ${row.ativo ? 'is-ok' : 'is-muted'}`}>
                                                {row.ativo ? 'ativo' : 'inativo'}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`fin-pp__pill ${row.tipo === 'disparo' ? 'is-info' : 'is-warn'}`}>
                                                {TIPOS[row.tipo] ?? row.tipo}
                                            </span>
                                        </td>
                                        <td className="fin-pp__cell-strong">{row.nome}</td>
                                        <td className="fin-pp__cell-muted">{nomeOrigem(row.origem)}</td>
                                        {/* campo vazio = "a relação existe?" */}
                                        <td className="fin-pp__cell-muted">{row.campo || 'existe o registro'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{criterios.length} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage((state) => state - 1)}>
                                Anterior
                            </button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((state) => state + 1)}>
                                Próxima
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
